import { Link } from "react-router-dom";
import { useState } from "react";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";

// Utility function to format numbers with commas
const formatCurrency = (amount: number): string => {
  return amount.toLocaleString();
};

export default function FeaturedWorkspaceCard(workspace: WorkspaceCardData) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handlePreviousImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? workspace.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === workspace.images.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Link
      to={`/workspace/${workspace.workspaceId}`}
      className="block rounded-2xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 h-full relative cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative w-full h-full">
        <img
          src={
            workspace.images[currentImageIndex]?.imageUrl ||
            workspace.images[0]?.imageUrl
          }
          alt={workspace.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />

        {/* Image Navigation Arrows */}
        {workspace.images.length > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={handlePreviousImage}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 z-30"
            >
              <svg
                className="w-5 h-5 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Right Arrow */}
            <button
              onClick={handleNextImage}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 z-30"
            >
              <svg
                className="w-5 h-5 text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Image Indicators */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
              {workspace.images.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    currentImageIndex === index
                      ? "bg-white"
                      : "bg-white bg-opacity-50 hover:bg-opacity-75"
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Hover Overlay with Information */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6 transition-opacity duration-300 z-20 ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="text-white">
            <div className="flex justify-between items-start mb-3">
              <h2 className="text-xl md:text-2xl font-bold line-clamp-2 flex-1">
                {workspace.title}
              </h2>
              <span className="bg-blue-500 bg-opacity-90 text-white text-xs font-medium px-3 py-1 rounded-full ml-3 flex-shrink-0">
                {workspace.workspaceType}
              </span>
            </div>

            <p className="text-gray-200 mb-4 line-clamp-2 text-sm md:text-base">
              {workspace.address}
            </p>

            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <span className="font-bold text-lg md:text-xl text-yellow-400">
                  ${formatCurrency(workspace.pricePerYear)}/year
                </span>
                <span className="text-xs md:text-sm text-gray-300">
                  Capacity: {formatCurrency(workspace.capacity)} people
                </span>
              </div>

              <div className="flex items-center bg-white bg-opacity-20 rounded-full px-3 py-1">
                <svg
                  className="w-4 h-4 text-yellow-400 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm font-medium">Featured</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
